import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import LoadingIndicator from '../../../LoadingIndicator/LoadingIndicator';

const ColumnFilterForm = styled.div`
  padding: 0;
  width: 250px;
`;

const ColumnFilterFormHeader = styled.div`
  padding: 10px 10px;
`;

const ColumnFilterFormList = styled.div`
  height: 350px;
  overflow-y: auto;
`;

const ColumnFilterFormListItem = styled(ListItem)`
  height: 30px;
  padding: 0;
  font-size: 0.8em;
  white-space: nowrap;
`;

const ColumnFilterFormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  padding: 5px 20px;
  & > div:first-child {
    flex: 1;
    text-align: left;
    display: flex;
    align-items: center;
  }
  & > div:last-child {
    flex: 1;
    text-align: right;
  }
`;

const DataTableColumnFilterForm = ({ data, setCurrentFilter = () => {}, currentFilter, onClose, loading }) => {
  const [list, setList] = useState(data);
  const [filter, setFilter] = useState(currentFilter || []);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setList(data);
  }, [data]);

  const toggleAllFilter = () => {
    if ((filter.length > 0 && filter.length < list.length) || filter.length === list.length) {
      setFilter([]);
    } else if (filter.length === 0) {
      setFilter(list.map((item) => item.id));
    }
  };

  const toggleLineFilter = (id) => {
    setFilter((prevFilterState) => {
      let output;
      if (prevFilterState.includes(id)) {
        output = prevFilterState.filter((item) => item !== id);
      } else {
        output = [...prevFilterState, id];
      }
      return output;
    });
  };

  const handleTextChange = (event) => {
    const text = event.target.value;
    setSearch(text);
  };

  const clearSearch = () => {
    setSearch('');
  };

  return (
    <ColumnFilterForm>
      <ColumnFilterFormHeader>
        <SearchTextField clearSearch={clearSearch} handleTextChange={handleTextChange} search={search} />
      </ColumnFilterFormHeader>
      <ColumnFilterFormList>
        {loading && <LoadingIndicator />}
        <DataList search={search} filter={filter} list={list} toggleAllFilter={toggleAllFilter} toggleLineFilter={toggleLineFilter} />
      </ColumnFilterFormList>
      <Divider />
      <ColumnFilterFormFooter>
        <div>
          <FormattedMessage id={'app.dataTable.columnFilter.checked'} />
          &nbsp;
          {list.length - filter.length}/{list.length}
        </div>
        <div>
          <Button
            variant="contained"
            color={'primary'}
            onClick={() => {
              setCurrentFilter(filter);
              onClose();
            }}>
            <FormattedMessage id={'app.dataTable.columnFilter.filter'} />
          </Button>
        </div>
      </ColumnFilterFormFooter>
    </ColumnFilterForm>
  );
};

DataTableColumnFilterForm.propTypes = {
  data: PropTypes.array,
  setCurrentFilter: PropTypes.func,
  currentFilter: PropTypes.array,
  onClose: PropTypes.func,
  loading: PropTypes.bool,
};

export default DataTableColumnFilterForm;

const SearchTextField = ({ search, handleTextChange, clearSearch }) => {
  const inputRef = useRef(null);
  const onClearSearch = () => {
    clearSearch();
    if (inputRef) {
      inputRef.current.focus();
    }
  };

  return (
    <TextField
      autoFocus
      onChange={handleTextChange}
      value={search}
      InputProps={{
        startAdornment: (
          <InputAdornment position={'start'}>
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position={'end'}>
            <IconButton onClick={onClearSearch}>
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ),
        inputRef: inputRef,
      }}
    />
  );
};

SearchTextField.propTypes = {
  search: PropTypes.string.isRequired,
  handleTextChange: PropTypes.func.isRequired,
  clearSearch: PropTypes.func.isRequired,
};

const DataList = ({ filter, list, search, toggleAllFilter, toggleLineFilter }) => (
  <List>
    <ColumnFilterFormListItem onClick={toggleAllFilter}>
      <Checkbox indeterminate={filter.length > 0 && filter.length < list.length} checked={filter.length === 0 || filter.length < list.length} />
      <ListItemText>
        <FormattedMessage id={'app.dataTable.columnFilter.all'} />
      </ListItemText>
    </ColumnFilterFormListItem>
    {list
      .filter((row) => {
        return row.label.toLowerCase().includes(search.toLowerCase()) || !search;
      })
      .map((row) => (
        <ColumnFilterFormListItem key={row.id}>
          <Checkbox onClick={() => toggleLineFilter(row.id)} checked={!filter.includes(row.id)} />
          <ListItemText onClick={() => toggleLineFilter(row.id)}>{row.label}</ListItemText>
        </ColumnFilterFormListItem>
      ))}
  </List>
);

DataList.propTypes = {
  filter: PropTypes.array.isRequired,
  list: PropTypes.array.isRequired,
  search: PropTypes.string.isRequired,
  toggleAllFilter: PropTypes.func.isRequired,
  toggleLineFilter: PropTypes.func.isRequired,
};
